/**
 * MODULE: _shared/lib/frontend/api/workspacePeerOrigin.ts
 * PURPOSE: Resolve Traditional ↔ Agentic peer origin for local Vite vs hosted same-origin
 * DEPENDENCIES: None (pure URL helper)
 * PUBLIC_API: isLocalHostname, resolveWorkspacePeerOrigin, WorkspaceLocationHint
 * SIDE_EFFECTS: None
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~55 / 150 lines
 * RECENT: 2026-09-15 [2.2.2138] — Hosted SPAs use current origin; localhost keeps :5173/:5174
 */

export type WorkspaceLocationHint = {
  hostname?: string;
  origin?: string;
};

export function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

function readWindowLocation(): WorkspaceLocationHint {
  if (typeof window === 'undefined') return {};
  try {
    return { hostname: window.location.hostname, origin: window.location.origin };
  } catch {
    return {};
  }
}

/**
 * Peer SPA origin for mode-switch / deep-links.
 * Explicit env wins; hosted (non-local) hosts use the current origin so
 * app.brazn.ai / dev / staging never fall back to localhost Vite ports.
 */
export function resolveWorkspacePeerOrigin(opts: {
  envOverride?: string | null;
  location?: WorkspaceLocationHint;
  localFallback: string;
}): string {
  const override = opts.envOverride?.trim();
  if (override) return stripTrailingSlash(override);

  const loc = opts.location ?? readWindowLocation();
  const hostname = loc.hostname?.trim() ?? '';
  const origin = loc.origin?.trim() ?? '';
  if (hostname && origin && !isLocalHostname(hostname)) {
    return stripTrailingSlash(origin);
  }
  return stripTrailingSlash(opts.localFallback);
}
